// 마이클 콘텐츠 도메인 타입·상수 정본은 @/entities/content 로 이동.
// 내부 참조·하위호환을 위해 import 후 re-export 한다.
import {
  MYCLE_CONTENT_MENU,
  MYCLE_CONTENT_SERIES_CODE,
  type TMycleContentMenu,
  type TMycleContentSeriesCode,
  type IMycleContentDetailResponse,
} from '@/entities/content'

export { MYCLE_CONTENT_MENU, MYCLE_CONTENT_SERIES_CODE }
export type {
  TMycleContentMenu,
  TMycleContentSeriesCode,
  IMycleContentDetailResponse,
}

/** 메뉴별 최신 게시 시각 키 (백엔드가 임의로 추가 가능하므로 string 으로 받음) */
export type TCarStoryMenuCode = 'CAR' | 'DRIVE' | 'NOW' | string

/** 콘텐츠 통계 요약 */
export interface IMycleContentStat {
  viewCount: number
  likeCount: number
}

/** 콘텐츠 카드 (썸네일 + 제목, 통계는 응답에 따라 optional) */
export interface IMycleContentCard {
  serverId: number
  title: string
  thumbnailUrl: string
  stat?: IMycleContentStat
}

/** 트렌딩 콘텐츠 (stat 필수) */
export interface IMycleContentTrending extends IMycleContentCard {
  stat: IMycleContentStat
}

/** 상단 메인 콘텐츠 라벨/컬러 메타 */
export interface IMycleContentMainMeta {
  label: string
  bgColor: string
  textColor: string
}

/** 상단 메인 콘텐츠 (카드 + 메인 메타) */
export interface IMycleContentMain extends IMycleContentCard {
  main: IMycleContentMainMeta
  /** 콘텐츠 메뉴 (NOW만 contentUrl 사용) */
  menu?: TMycleContentMenu
  /** 외부 이벤트 페이지 URL (NOW 메뉴 전용 — 클릭 시 내부 상세 대신 이 URL로 이동) */
  contentUrl?: string | null
}

/** 시리즈 콘텐츠 항목별 메타 */
export interface IMycleContentSeriesMeta {
  /** 정비사 시리즈 전용 */
  mechanicInfo?: string
  /** 일반 시리즈에서 사용 */
  bgColor?: string | null
  textColor?: string | null
}

/** 시리즈 콘텐츠 항목 (카드 + 시리즈 메타) */
export interface IMycleContentSeriesItem extends IMycleContentCard {
  series: IMycleContentSeriesMeta
}

/** 메뉴별 콘텐츠 목록 조회 파라미터 */
export interface IGetMycleContentsParams {
  menu: TMycleContentMenu
  page?: number
  size?: number
  /** mycleContent API의 pageable.sort 값 (예: createTime,desc) */
  sort?: string
  /** 카테고리 serverId. 미지정(전체) 시 전체 콘텐츠 조회 */
  categoryId?: number
}

/** 마이클 콘텐츠 카테고리 */
export interface IMycleContentCategory {
  serverId: number
  code: string
  menu: TMycleContentMenu
  name: string
  sortOrder: number
}

/** 메뉴별 카테고리 목록 조회 파라미터 */
export interface IGetMycleContentCategoriesParams {
  menu: TMycleContentMenu
}

/** 시리즈 코드별 콘텐츠 목록 조회 파라미터 */
export interface IGetMycleContentsBySeriesParams {
  code: TMycleContentSeriesCode
  page?: number
  size?: number
}

/** 시리즈 코드별 콘텐츠 목록 페이지네이션 응답 */
export interface IPageMycleContentSeriesResponse {
  content: IMycleContentSeriesItem[]
  totalElements: number
  totalPages: number
  number: number
  size: number
  first: boolean
  last: boolean
  empty: boolean
  numberOfElements: number
}

/** 메뉴별 콘텐츠 목록 항목 (카드 + 카테고리 + 프로모션 기간) */
export interface IMycleContentListItem extends IMycleContentCard {
  /** 콘텐츠 메뉴 (NOW만 contentUrl 사용) */
  menu?: TMycleContentMenu
  /** 카테고리 이름 목록 */
  categoryNames: string[]
  /** 프로모션 시작 일시 (NOW 메뉴 전용, ISO date-time) */
  promotionStartDateTime?: string | null
  /** 프로모션 종료 일시 (NOW 메뉴 전용, ISO date-time) */
  promotionEndDateTime?: string | null
  /** 외부 이벤트 페이지 URL (NOW 메뉴 전용 — 클릭 시 내부 상세 대신 이 URL로 이동) */
  contentUrl?: string | null
}

/** 메뉴별 콘텐츠 목록 페이지네이션 응답 */
export interface IPageMycleContentResponse {
  content: IMycleContentListItem[]
  totalElements: number
  totalPages: number
  number: number
  size: number
  first: boolean
  last: boolean
  empty: boolean
  numberOfElements: number
}

/** 마이클 콘텐츠 좋아요 상태 응답 */
export interface IMycleContentLikeResponse {
  liked: boolean
}

/** 마이클 콘텐츠 홈 탭 응답 */
export interface ICarStoryHomeResponse {
  /** 메뉴별 최신 게시 시각 (date-time string, hasNew 계산용) */
  latestPublishedAt: Record<TCarStoryMenuCode, string>
  /** 상단 메인 콘텐츠 (최신순) */
  mainContents: IMycleContentMain[]
  /** 지금 뜨는 이야기 (top 4) */
  trending: IMycleContentTrending[]
  /** 차량관리 (최신 4) */
  carContents: IMycleContentCard[]
  /** 드라이브 (최신 4) */
  driveContents: IMycleContentCard[]
  /** 마이클 정비사 시리즈 콘텐츠 목록 */
  mechanicSeries: IMycleContentSeriesItem[]
}
